import { CapTableDetailsModel, FundOwnershipDetailModel } from 'api';
import { Logger } from 'utilities';

type SecurityLike = {
  id?: number | null;
  name?: string | null;
};

const buildUniqueNameToIdMap = (securities: SecurityLike[] | null | undefined) => {
  const nameCounts = new Map<string, number>();
  (securities ?? []).forEach(security => {
    if (security.name) {
      nameCounts.set(security.name, (nameCounts.get(security.name) ?? 0) + 1);
    }
  });

  const nameToId = new Map<string, number>();
  (securities ?? []).forEach(security => {
    if (security.id == null || !security.name) {
      return;
    }
    // Duplicate names are ambiguous across versions — skip rather than mis-attribute.
    if ((nameCounts.get(security.name) ?? 0) > 1) {
      return;
    }
    nameToId.set(security.name, security.id);
  });
  return nameToId;
};

// Remap primary FO security ids onto a target (selected) cap table's securities by name.
// Related-entity investments reference the child's own securities, so preserve
// those rows verbatim instead of treating them as unmappable primary rows.
// Returns a new FO object; never mutates the API response.
// Returns null if any security-bearing FOD cannot be mapped (fail closed — no understated values).
const remapFundOwnershipToTargetSecurities = (
  primaryFundOwnership: CapTableDetailsModel['fund_ownership'],
  primarySecurities: SecurityLike[] | null | undefined,
  targetSecurities: SecurityLike[] | null | undefined,
  sourceCompanyId: number
): CapTableDetailsModel['fund_ownership'] | null => {
  if (!primaryFundOwnership) {
    return primaryFundOwnership;
  }

  const primaryIdToName = new Map(
    (primarySecurities ?? [])
      .filter(
        (security): security is SecurityLike & { id: number; name: string } =>
          security.id != null && Boolean(security.name)
      )
      .map(security => [security.id, security.name])
  );

  const nameToTargetId = buildUniqueNameToIdMap(targetSecurities);
  const sourceDetails = primaryFundOwnership.fund_ownership_detail ?? [];

  const remappedDetails: FundOwnershipDetailModel[] = [];
  let droppedCount = 0;

  sourceDetails.forEach((fod: FundOwnershipDetailModel) => {
    // CFL-only synthetics (no security) pass through unchanged.
    if (fod.security == null) {
      remappedDetails.push(fod);
      return;
    }

    const belongsToRelatedEntity =
      fod.entity_reference != null && Number(fod.entity_reference) !== sourceCompanyId;
    if (belongsToRelatedEntity) {
      remappedDetails.push(fod);
      return;
    }

    const securityName = primaryIdToName.get(fod.security as number);
    if (!securityName) {
      droppedCount += 1;
      return;
    }

    const targetSecurityId = nameToTargetId.get(securityName);
    if (targetSecurityId == null) {
      droppedCount += 1;
      return;
    }

    remappedDetails.push({
      ...fod,
      security: targetSecurityId,
    });
  });

  if (droppedCount > 0) {
    Logger.warn(
      `Dropped ${droppedCount} fund ownership detail(s) while remapping primary FO onto the selected cap table by security name; aborting remap`
    );
    return null;
  }

  return {
    ...primaryFundOwnership,
    fund_ownership_detail: remappedDetails,
  };
};

export default remapFundOwnershipToTargetSecurities;
